/**
 * The words of the home page. The page shows them, and the build writes the same words into index.html
 * (scripts/prerender.ts) for search engines and AI assistants that read a page without running it.
 */
import type { LucideIcon } from "lucide-react";
import { BookOpen, Bot, KeyRound, ListChecks, LockKeyhole, Mail, NotebookPen, PenLine, Smartphone, Sparkles, Table2 } from "lucide-react";

export const YOUTUBE_CHANNEL = "https://www.youtube.com/@BrainLogApp";
/** "BrainLog in two minutes". */
export const YOUTUBE_TOUR = "https://youtu.be/aYqUeW9vyaM";
export const LINKEDIN_PAGE = "https://www.linkedin.com/company/146729660/";

export const HERO = {
  title: "Your research, in one calm place.",
  text: "Notes, papers and writing together, with AI that shows where every answer comes from, and no AI jargon to learn. From the first reading to the final reply to reviewers.",
};

export const FEATURES_INTRO = {
  title: "Everything a research project needs",
  text: "One place instead of five apps, so nothing gets lost between your reading, your notes and your paper.",
};

/** The path of a research project, in six steps. */
export const FEATURES: { icon: LucideIcon; title: string; text: string }[] = [
  {
    icon: NotebookPen,
    title: "Notes and papers, together",
    text: "Notebooks and projects for your notes, and a library for your papers: add one by its DOI or PDF, or bring your Zotero or Mendeley collections.",
  },
  { icon: BookOpen, title: "Read papers in depth", text: "A clear reading note for any paper, with quotes that link to the exact page they come from." },
  { icon: Table2, title: "Compare papers side by side", text: "The literature matrix: question, methods, findings and limits of each paper, from your reading notes. Or ask where they agree and differ." },
  { icon: PenLine, title: "Write and revise your paper", text: "Cite from your library as you type, get review rounds, answer reviewers point by point, then send it to Overleaf or download it for Word." },
  { icon: Mail, title: "Meetings and emails become tasks", text: "Action items in your meeting notes and journal emails become tasks, with owners and due dates, in one backlog." },
  { icon: LockKeyhole, title: "Private, or shared your way", text: "Lock a notebook so only you and the people you choose can read it, or share a project page with a password and an end date." },
];

/** Everything else, in a line under the six. */
export const FEATURES_MORE = [
  "Writing together in real time",
  "A knowledge base of each project",
  "Briefings by email or Telegram",
  "Code and data with their checks",
  "Works with Claude and ChatGPT",
  "Leave anytime with all your data",
];

export const AI_INTRO = {
  title: "AI, skills and agents in one place",
  text: "Made for researchers, not AI experts: nothing to install, no prompts to write. AI always shows its sources, and you decide when it is used.",
};

/** The words people hear about AI, each with what it does for a researcher in BrainLog. */
export const AI_WORDS: { icon: LucideIcon; word: string; title: string; text: string }[] = [
  {
    icon: Sparkles,
    word: "AI",
    title: "Buttons, not prompts",
    text: "Choose what you need: read a paper in depth, compare papers, review your manuscript. BrainLog writes the instructions for the AI, and every answer shows where it comes from.",
  },
  {
    icon: ListChecks,
    word: "Skills",
    title: "Methods of experienced researchers",
    text: "A skill is a method the AI follows step by step, such as Critical appraisal or Reviewer panel. Many come ready to use; add your own, or a skill file made for Claude.",
  },
  {
    icon: Bot,
    word: "Agents",
    title: "Your AI app, working in your notebook",
    text: "An agent is an AI app, such as Claude or ChatGPT, that can do tasks for you. Connect yours in a few clicks: it reads and adds to your notes, never deletes, and every change is kept.",
  },
  {
    icon: Smartphone,
    word: "Telegram",
    title: "Ask from your phone",
    text: "Ask a question about your project, send a DOI to add a paper, keep a quick note, or get your briefing at the hour you choose: like a message to a colleague.",
  },
];

/** The two ways AI is paid, in one line under the four words. */
export const AI_PAID: { icon: LucideIcon; text: string }[] = [
  { icon: KeyRound, text: "Your own AI key: free and unlimited, on every plan." },
  { icon: Sparkles, text: "Or AI included in the Researcher plans, nothing to set up." },
];

export const PRICING_INTRO = {
  title: "Simple pricing",
  text: "Start free. Pay only for more space, or for AI you don't want to set up yourself.",
  footnote: "Prices include VAT. Cancel anytime: your notes stay yours, and you can always export them.",
};

export const FAQ: { q: string; a: string }[] = [
  {
    q: "I'm new to AI. Where do I start?",
    a: "With a button. Open a paper and choose to read it in depth, or ask your project a question in your own words. There are no prompts to write and no model to choose: good defaults are set, and every result shows its sources so you can check it.",
  },
  {
    q: "What do “model”, “skill” and “agent” mean?",
    a: "A model is the AI itself, such as Claude or GPT, made by an AI company. A skill is a method the AI follows step by step. An agent is an AI app that can do tasks for you, such as Claude or ChatGPT connected to BrainLog. You can use BrainLog without knowing any of these words.",
  },
  {
    q: "What is “your own AI key”?",
    a: "A personal key from an AI company, such as Anthropic (Claude) or OpenAI. You paste it into your settings once, and BrainLog uses it for your AI requests. You pay the AI company directly for what you use, often a few euros a month.",
  },
  {
    q: "Is my research private?",
    a: "Your notes are visible only to you and the people you share them with. We don't sell data, show ads or train AI on your work. For extra privacy, lock a notebook with encryption, or switch AI off for a notebook.",
  },
  {
    q: "What happens when my space is full?",
    a: "Nothing is lost or locked. You can keep reading, writing and exporting; only new file uploads pause until you free some space or upgrade.",
  },
  { q: "Can I leave with my data?", a: "Yes, at any time: notes as Markdown files, your library as BibTeX or RIS, your files as they are." },
  { q: "Do I need Zotero or Mendeley?", a: "No. BrainLog has its own library. If you already use one of them, connect it and keep working as before." },
  {
    q: "What will the assistant send me?",
    a: "Only what you choose, where you choose: a briefing by email or Telegram at your hour (daily on the Researcher plans, weekly on Free), and everything in the app's notifications. Never about what you did yourself, nothing when there is nothing new, quiet hours, a pause, and one click to stop.",
  },
  { q: "Can I use it with my team?", a: "Yes. Share any notebook or project with colleagues. On the Lab plan, storage and AI are shared across the group." },
];
